import React from 'react';
import { Info } from 'lucide-react';
import { toast } from 'sonner';
import {
    Alert,
    AlertDescription,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    Spinner,
} from '@/Components/shadcn';
import { useLinkedInMessageOutreach } from '@/Pages/Candidates/AiSearch/api/useOutreachCandidate';
import { useOutreachCandidatePopoutContext } from '../OutreachContext';
import LinkedInMessageContainer from './LinkedInMessageContainer';
import { useShortlistedTableContext } from '../../utils/ShortlistedTableContext';
import { useShortlistedListContext } from '../../utils/ShortlistedListContext';
import { useUpdateShortlistedProfilesStatus } from '@/Pages/Candidates/AiSearch/api/useShortlistedProfiles';
import UIStrings from '@/Utils/UIStrings';
import BlueRibbonDialog from '../../components/BlueRibbonDialog';

const LinkedInMessageDialog = () => {
    const { selectedProfile, openLinkedInMessage, closeOutreachPopout } = useOutreachCandidatePopoutContext();
    const { isLoading } = useLinkedInMessageOutreach();
    const { trigger: updateStatus, isMutating: isUpdatingStatus } = useUpdateShortlistedProfilesStatus();
    const { mutate: mutateShortlistedProfiles } = useShortlistedListContext();
    const { resetRowSelection } = useShortlistedTableContext();

    const handleCloseDialog = async (isMessageCopied?: boolean) => {
        if (isUpdatingStatus) return;

        try {
            await updateStatus({
                shortlistedProfilesIds: selectedProfile.map(({ id }) => id),
                shortlistedStatus: 'ContactTaskInMail',
            });
            mutateShortlistedProfiles();
            toast.success(
                isMessageCopied
                    ? UIStrings.MessageCopiedToClipboardStatusUpdated
                    : UIStrings.NumberOfCandidateStatus(1, UIStrings.ContactTaskInMail),
            );
            resetRowSelection(true);
        } catch {
            toast.error(
                isMessageCopied
                    ? UIStrings.SomethingWentWrongDuringStatusUpdate
                    : UIStrings.SomethingWentWrongPleaseTryAgainLater,
            );
        } finally {
            closeOutreachPopout();
        }
    };

    return (
        <Dialog
            open={openLinkedInMessage}
            onOpenChange={(next) => {
                if (!next) closeOutreachPopout();
            }}
        >
            <DialogContent className="max-h-10/12 max-w-5xl gap-0 overflow-auto rounded-lg border-0 p-0 shadow-xl">
                <DialogHeader>
                    <DialogTitle className="pl-6">
                        <BlueRibbonDialog>{UIStrings.LinkedInOutreach}</BlueRibbonDialog>
                    </DialogTitle>
                    <DialogDescription>
                        <Alert variant="info" className="mx-6 mt-2 w-auto">
                            <Info />
                            <AlertDescription className="text-inherit">
                                {UIStrings.CopyAndPasteItManuallyIntoLinkedIn}
                            </AlertDescription>
                        </Alert>
                    </DialogDescription>
                </DialogHeader>
                <div className="flex w-full flex-col">
                    <div className="p-6 pt-4">
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center gap-3 py-10">
                                <Spinner className="text-primary size-10" />
                            </div>
                        ) : (
                            <LinkedInMessageContainer onClose={handleCloseDialog} />
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LinkedInMessageDialog;
